import {_RequestEnvelope, FieldUpdate} from '../core/types';
import {
    FakeReviewAuthor,
    FakeReviewAuthorAddDto,
    FakeReviewComment,
    FakeReviewCommentAddDto,
    GameReview,
    GameReviewKindFilter,
    RatingCounts,
    RatingStats,
    RemoveStrategy,
} from '../models/review';

// ---------------------------------------------------------------------------
// AddFakeAuthors
// ---------------------------------------------------------------------------

export interface AddFakeAuthorsRequest {
    Authors: FakeReviewAuthorAddDto[];
}

export interface AddFakeAuthorsResponse {
    InsertedCount: number;
}

export function addFakeAuthors(params: AddFakeAuthorsRequest): _RequestEnvelope<AddFakeAuthorsResponse> {
    return {...params, _endpoint: 'admin/review/addFakeAuthors', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// AddFakeComments
// ---------------------------------------------------------------------------

export interface AddFakeCommentsRequest {
    Comments: FakeReviewCommentAddDto[];
}

export interface AddFakeCommentsResponse {
    InsertedCount: number;
}

export function addFakeComments(params: AddFakeCommentsRequest): _RequestEnvelope<AddFakeCommentsResponse> {
    return {...params, _endpoint: 'admin/review/addFakeComments', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// AddFakeGameReview
// ---------------------------------------------------------------------------

export interface AddFakeGameReviewRequest {
    RealmId: string;
    GameId: string;
    UserId: string;
    Rating: number;
    Comment: string;
    CreatedAt: string;
}

export const AddFakeGameReviewResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    InvalidAuthor: 'InvalidAuthor',
    AuthorAlreadyUsed: 'AuthorAlreadyUsed',
} as const;
export type AddFakeGameReviewResult =
    typeof AddFakeGameReviewResultCode[keyof typeof AddFakeGameReviewResultCode];

export type AddFakeGameReviewResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Review: GameReview;
}
    | {
    Result: 'Unknown' | 'InvalidAuthor' | 'AuthorAlreadyUsed';
    ResultDescription: string;
    Review: null;
};

const _addFakeGameReviewMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'InvalidAuthor',
    3: 'AuthorAlreadyUsed',
};

export function addFakeGameReview(
    params: AddFakeGameReviewRequest
): _RequestEnvelope<AddFakeGameReviewResponse> {
    return {...params, _endpoint: 'admin/review/addFakeGameReview', _resultCodeMap: _addFakeGameReviewMap};
}

// ---------------------------------------------------------------------------
// AddFakeReviews
// ---------------------------------------------------------------------------

export interface AddFakeReviewsRequest {
    RealmId: string;
    GameId: string;
    RatingCounts: RatingCounts;
    Tags: string[];
    CreatedFrom: string;
    CreatedTo: string;
}

export const AddFakeReviewsResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    InsufficientAuthors: 'InsufficientAuthors',
    InsufficientComments: 'InsufficientComments',
} as const;
export type AddFakeReviewsResult =
    typeof AddFakeReviewsResultCode[keyof typeof AddFakeReviewsResultCode];

export type AddFakeReviewsResponse =
    | {
    Result: 'Ok' | 'InsufficientAuthors' | 'InsufficientComments';
    ResultDescription: string;
    CreatedCount: number;
    Stats: RatingStats;
    AvailableAuthors: number;
    RequestedAuthors: number;
    CommentsRating: number;
    AvailableComments: number;
    RequestedComments: number;
}
    | {
    Result: 'Unknown';
    ResultDescription: string;
    CreatedCount: null;
    Stats: null;
    AvailableAuthors: null;
    RequestedAuthors: null;
    CommentsRating: null;
    AvailableComments: null;
    RequestedComments: null;
};

const _addFakeReviewsMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'InsufficientAuthors',
    3: 'InsufficientComments',
};

export function addFakeReviews(
    params: AddFakeReviewsRequest
): _RequestEnvelope<AddFakeReviewsResponse> {
    return {...params, _endpoint: 'admin/review/addFakeReviews', _resultCodeMap: _addFakeReviewsMap};
}

// ---------------------------------------------------------------------------
// DeleteFakeComment
// ---------------------------------------------------------------------------

export interface DeleteFakeCommentRequest {
    Id: string;
}

export interface DeleteFakeCommentResponse {
    Deleted: boolean;
}

export function deleteFakeComment(params: DeleteFakeCommentRequest): _RequestEnvelope<DeleteFakeCommentResponse> {
    return {...params, _endpoint: 'admin/review/deleteFakeComment', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// DeleteFakeGameReview
// ---------------------------------------------------------------------------

export interface DeleteFakeGameReviewRequest {
    RealmId: string;
    Id: string;
}

export const DeleteFakeGameReviewResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    WrongType: 'WrongType',
} as const;
export type DeleteFakeGameReviewResult =
    typeof DeleteFakeGameReviewResultCode[keyof typeof DeleteFakeGameReviewResultCode];

export type DeleteFakeGameReviewResponse =
    {
        Result: DeleteFakeGameReviewResult;
    ResultDescription: string;
};

const _deleteFakeGameReviewMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'WrongType',
};

export function deleteFakeGameReview(
    params: DeleteFakeGameReviewRequest
): _RequestEnvelope<DeleteFakeGameReviewResponse> {
    return {...params, _endpoint: 'admin/review/deleteFakeGameReview', _resultCodeMap: _deleteFakeGameReviewMap};
}

// ---------------------------------------------------------------------------
// DeleteRealGameReview
// ---------------------------------------------------------------------------

export interface DeleteRealGameReviewRequest {
    RealmId: string;
    Id: string;
}

export const DeleteRealGameReviewResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    WrongType: 'WrongType',
} as const;
export type DeleteRealGameReviewResult =
    typeof DeleteRealGameReviewResultCode[keyof typeof DeleteRealGameReviewResultCode];

export type DeleteRealGameReviewResponse =
    {
        Result: DeleteRealGameReviewResult;
    ResultDescription: string;
};

const _deleteRealGameReviewMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'WrongType',
};

export function deleteRealGameReview(
    params: DeleteRealGameReviewRequest
): _RequestEnvelope<DeleteRealGameReviewResponse> {
    return {...params, _endpoint: 'admin/review/deleteRealGameReview', _resultCodeMap: _deleteRealGameReviewMap};
}

// ---------------------------------------------------------------------------
// GetFakeCommentsStat
// ---------------------------------------------------------------------------

export interface GetFakeCommentsStatRequest {
    Tags: string[] | null;
}

export interface GetFakeCommentsStatResponse {
    TotalCount: number;
    PerRating: RatingCounts;
}

export function getFakeCommentsStat(params: GetFakeCommentsStatRequest): _RequestEnvelope<GetFakeCommentsStatResponse> {
    return {...params, _endpoint: 'admin/review/getFakeCommentsStat', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// GetFakeReviewInfo
// ---------------------------------------------------------------------------

export interface GetFakeReviewInfoRequest {
    RealmId: string;
    GameId: string;
}

export interface GetFakeReviewInfoResponse {
    Stats: RatingStats;
}

export function getFakeReviewInfo(params: GetFakeReviewInfoRequest): _RequestEnvelope<GetFakeReviewInfoResponse> {
    return {...params, _endpoint: 'admin/review/getFakeReviewInfo', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListFakeAuthors
// ---------------------------------------------------------------------------

export interface ListFakeAuthorsRequest {
    ActiveOnly: boolean;
    Skip: number;
    Limit: number;
}

export interface ListFakeAuthorsResponse {
    Authors: FakeReviewAuthor[];
}

export function listFakeAuthors(params: ListFakeAuthorsRequest): _RequestEnvelope<ListFakeAuthorsResponse> {
    return {...params, _endpoint: 'admin/review/listFakeAuthors', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListFakeComments
// ---------------------------------------------------------------------------

export interface ListFakeCommentsRequest {
    Tags: string[] | null;
    Skip: number;
    Limit: number;
}

export interface ListFakeCommentsResponse {
    Comments: FakeReviewComment[];
}

export function listFakeComments(params: ListFakeCommentsRequest): _RequestEnvelope<ListFakeCommentsResponse> {
    return {...params, _endpoint: 'admin/review/listFakeComments', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListGameReviews
// ---------------------------------------------------------------------------

export interface ListGameReviewsRequest {
    RealmId: string;
    GameId: string | null;
    Filter: GameReviewKindFilter;
    Skip: number;
    Limit: number;
}

export interface ListGameReviewsResponse {
    Reviews: GameReview[];
}

export function listGameReviews(params: ListGameReviewsRequest): _RequestEnvelope<ListGameReviewsResponse> {
    return {...params, _endpoint: 'admin/review/listGameReviews', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// RemoveAllFakeReviews
// ---------------------------------------------------------------------------

export interface RemoveAllFakeReviewsRequest {
    RealmId: string;
    GameId: string;
}

export interface RemoveAllFakeReviewsResponse {
    RemovedCount: number;
}

export function removeAllFakeReviews(params: RemoveAllFakeReviewsRequest): _RequestEnvelope<RemoveAllFakeReviewsResponse> {
    return {...params, _endpoint: 'admin/review/removeAllFakeReviews', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// RemoveFakeReviews
// ---------------------------------------------------------------------------

export interface RemoveFakeReviewsRequest {
    RealmId: string;
    GameId: string;
    Count: number;
    Strategy: RemoveStrategy;
}

export interface RemoveFakeReviewsResponse {
    RemovedCount: number | null;
}

export function removeFakeReviews(params: RemoveFakeReviewsRequest): _RequestEnvelope<RemoveFakeReviewsResponse> {
    return {...params, _endpoint: 'admin/review/removeFakeReviews', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ReplaceFakeReviews
// ---------------------------------------------------------------------------

export interface ReplaceFakeReviewsRequest {
    RealmId: string;
    GameId: string;
    RatingCounts: RatingCounts;
    Tags: string[];
    CreatedFrom: string;
    CreatedTo: string;
}

export const ReplaceFakeReviewsResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    InsufficientAuthors: 'InsufficientAuthors',
    InsufficientComments: 'InsufficientComments',
} as const;
export type ReplaceFakeReviewsResult =
    typeof ReplaceFakeReviewsResultCode[keyof typeof ReplaceFakeReviewsResultCode];

export type ReplaceFakeReviewsResponse =
    | {
    Result: 'Ok' | 'InsufficientAuthors' | 'InsufficientComments';
    ResultDescription: string;
    RemovedCount: number;
    CreatedCount: number;
    Stats: RatingStats;
    AvailableAuthors: number;
    RequestedAuthors: number;
    CommentsRating: number;
    AvailableComments: number;
    RequestedComments: number;
}
    | {
    Result: 'Unknown';
    ResultDescription: string;
    RemovedCount: null;
    CreatedCount: null;
    Stats: null;
    AvailableAuthors: null;
    RequestedAuthors: null;
    CommentsRating: null;
    AvailableComments: null;
    RequestedComments: null;
};

const _replaceFakeReviewsMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'InsufficientAuthors',
    3: 'InsufficientComments',
};

export function replaceFakeReviews(
    params: ReplaceFakeReviewsRequest
): _RequestEnvelope<ReplaceFakeReviewsResponse> {
    return {...params, _endpoint: 'admin/review/replaceFakeReviews', _resultCodeMap: _replaceFakeReviewsMap};
}

// ---------------------------------------------------------------------------
// UpdateFakeAuthor
// ---------------------------------------------------------------------------

export interface UpdateFakeAuthorRequest {
    Id: string;
    Username: string | null;
    AvatarUrl: FieldUpdate<string | null>;
    IsActive: boolean | null;
}

export const UpdateFakeAuthorResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type UpdateFakeAuthorResult =
    typeof UpdateFakeAuthorResultCode[keyof typeof UpdateFakeAuthorResultCode];

export type UpdateFakeAuthorResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Author: FakeReviewAuthor;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Author: null;
};

const _updateFakeAuthorMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function updateFakeAuthor(
    params: UpdateFakeAuthorRequest
): _RequestEnvelope<UpdateFakeAuthorResponse> {
    return {...params, _endpoint: 'admin/review/updateFakeAuthor', _resultCodeMap: _updateFakeAuthorMap};
}

// ---------------------------------------------------------------------------
// UpdateFakeComment
// ---------------------------------------------------------------------------

export interface UpdateFakeCommentRequest {
    Id: string;
    Text: string | null;
    Tags: string[] | null;
    MinRating: number | null;
    MaxRating: number | null;
}

export const UpdateFakeCommentResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    InvalidRatingRange: 'InvalidRatingRange',
} as const;
export type UpdateFakeCommentResult =
    typeof UpdateFakeCommentResultCode[keyof typeof UpdateFakeCommentResultCode];

export type UpdateFakeCommentResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Comment: FakeReviewComment;
}
    | {
    Result: 'Unknown' | 'NotFound' | 'InvalidRatingRange';
    ResultDescription: string;
    Comment: null;
};

const _updateFakeCommentMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'InvalidRatingRange',
};

export function updateFakeComment(
    params: UpdateFakeCommentRequest
): _RequestEnvelope<UpdateFakeCommentResponse> {
    return {...params, _endpoint: 'admin/review/updateFakeComment', _resultCodeMap: _updateFakeCommentMap};
}

// ---------------------------------------------------------------------------
// UpdateFakeGameReview
// ---------------------------------------------------------------------------

export interface UpdateFakeGameReviewRequest {
    RealmId: string;
    Id: string;
    UserId: string | null;
    Rating: number | null;
    Comment: string | null;
    CreatedAt: string | null;
}

export const UpdateFakeGameReviewResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    WrongType: 'WrongType',
    InvalidAuthor: 'InvalidAuthor',
    AuthorAlreadyUsed: 'AuthorAlreadyUsed',
} as const;
export type UpdateFakeGameReviewResult =
    typeof UpdateFakeGameReviewResultCode[keyof typeof UpdateFakeGameReviewResultCode];

export type UpdateFakeGameReviewResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Review: GameReview;
}
    | {
    Result: 'Unknown' | 'NotFound' | 'WrongType' | 'InvalidAuthor' | 'AuthorAlreadyUsed';
    ResultDescription: string;
    Review: null;
};

const _updateFakeGameReviewMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'WrongType',
    4: 'InvalidAuthor',
    5: 'AuthorAlreadyUsed',
};

export function updateFakeGameReview(
    params: UpdateFakeGameReviewRequest
): _RequestEnvelope<UpdateFakeGameReviewResponse> {
    return {...params, _endpoint: 'admin/review/updateFakeGameReview', _resultCodeMap: _updateFakeGameReviewMap};
}
